import { useTheme } from '@backoffice-frontend/patterns'
import { HomeIcon } from '@moia-dev/pace-icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@moia-dev/pace-web'

export const PureHubMarker = ({
  hubName,
  isClickable = false,
  isFaded = false,
  isSelected = false,
  returningVehiclesCount,
  showReturningVehiclesLabel = false,
  vehicleCount,
}: {
  hubName: string
  isClickable?: boolean
  isFaded?: boolean
  isSelected?: boolean
  returningVehiclesCount?: number
  showReturningVehiclesLabel?: boolean
  vehicleCount?: number
}) => {
  const theme = useTheme()

  const hasVehicleCount = !!vehicleCount && vehicleCount > 0
  const hasReturningVehicleCount =
    !!returningVehiclesCount && returningVehiclesCount > 0

  const styles = {
    root: {
      alignItems: 'center',
      backgroundColor: theme.semantic.ColorSurfaceDefault,
      border: `1px solid ${theme.semantic.ColorBackgroundDimmedReduced}`,
      borderRadius: 4,
      cursor: 'default',
      display: 'flex',
      flexWrap: 'wrap',
      height: 32,
      justifyContent: 'center',
      position: 'relative',
      width: 32,
      zIndex: 3,

      '&:hover': {
        ...(!isSelected && {
          backgroundColor: theme.semantic.ColorSurfaceHovered,

          '& .vehicleCountLabel': {
            background: theme.semantic.ColorActionSecondaryHovered,
          },
        }),
      },

      ...(!isSelected && {
        '&:active': {
          backgroundColor: theme.semantic.ColorSurfacePressed,

          '& .vehicleCountLabel': {
            background: theme.semantic.ColorActionSecondaryPressed,
          },
        },
      }),
    } as const,

    selected: {
      '& .vehicleCountLabel': {
        background: theme.semantic.ColorAccentDefault,
      },

      '& svg': {
        color: theme.semantic.ColorAccentDefault,
      },
    },

    withCount: {
      flexFlow: 'row',
      width: 'auto',
    },

    iconWithCount: {
      marginLeft: 4,
      marginRight: 4,
    },

    markerContent: {
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      color: theme.semantic.ColorActionSecondaryDefault,
    },

    commonLabelStyles: {
      alignItems: 'center',
      display: 'flex',
      fontWeight: 'bold',
      fontSize: 16,
      height: '100%',
      padding: `0 ${theme.space.Space2}`,
      transition: 'background 0.3s ease-in-out',
    },

    vehicleCountLabel: {
      background: isSelected
        ? theme.semantic.ColorAccentDefault
        : theme.semantic.ColorBackgroundDimmedDefault,
      borderBottomRightRadius:
        showReturningVehiclesLabel && hasReturningVehicleCount ? 0 : 4,
      borderTopRightRadius:
        showReturningVehiclesLabel && hasReturningVehicleCount ? 0 : 4,
      color: theme.semantic.ColorContentOnAccentDefault,
    },

    returningVehiclesCountLabel: {
      background: theme.semantic.ColorBackgroundAlternative,
      color: theme.semantic.ColorAccentDefault,
      borderBottomRightRadius: 4,
      borderTopRightRadius: 4,
    },

    clickable: {
      cursor: 'pointer',
    },

    faded: {
      opacity: 0.5,
      transition: 'opacity 0.3s ease-in-out',
    },
  }

  return (
    <Tooltip placement="top" delay={0}>
      <TooltipTrigger>
        <div
          css={{
            ...styles.root,
            ...(hasVehicleCount && styles.withCount),
            ...(isFaded && styles.faded),
            ...(isClickable && styles.clickable),
            ...(isSelected && styles.selected),
          }}
        >
          <div className="markerContent" css={styles.markerContent}>
            <HomeIcon
              css={{
                height: '20px',
                width: '20px',
                ...(hasVehicleCount && styles.iconWithCount),
              }}
            />

            {hasVehicleCount && (
              <span
                className="vehicleCountLabel"
                css={[styles.commonLabelStyles, styles.vehicleCountLabel]}
              >
                {vehicleCount}
              </span>
            )}

            {showReturningVehiclesLabel && hasReturningVehicleCount && (
              <span
                className="returningVehiclesCountLabel"
                css={[
                  styles.commonLabelStyles,
                  styles.returningVehiclesCountLabel,
                ]}
              >
                +{returningVehiclesCount}
              </span>
            )}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>{hubName}</TooltipContent>
    </Tooltip>
  )
}
